import { memo } from "react";

/**
 * The Cursed Apple, drawn as page architecture.
 *
 * Deadlock is set in an occult 1900s New York whose skyline is modelled on
 * real buildings of the period: art deco setbacks, elevated railways, art
 * nouveau streetlamps, water towers.
 *
 * Two earlier attempts at this backdrop were wrong in instructive ways.
 * Blurred colour blobs read as a generic template. Then outlined buildings
 * read as a wireframe diagram, because a night skyline is not made of
 * outlines — it is solid mass against a lit sky, and the only bright things
 * are the windows. So:
 *
 *   - Buildings are filled silhouettes with NO stroke.
 *   - Three depth layers, each darker and larger than the one behind, which
 *     is what produces distance.
 *   - Windows are WARM amber against the cool sky. Warm-on-cool is the whole
 *     reason a night skyline reads; teal windows on a teal sky vanish.
 *   - An elevated railway crosses the foreground, the most recognisable
 *     structure in that period of the city.
 *   - Ziplines run rooftop to rooftop, the map's actual traversal network.
 */

type Variant = "skyline" | "arch" | "colonnade";

/* Memoised: it takes plain props and never changes, and the homepage
   re-renders on every card hover (the focus blur), which used to rebuild the
   whole city each time. */
export default memo(DecoBackdrop);

function DecoBackdrop({
  variant = "skyline",
  className = "",
  tone = "teal",
}: {
  variant?: Variant;
  className?: string;
  tone?: "teal" | "magenta";
}) {
  const stroke = tone === "magenta" ? "#ec4899" : "#2dd4bf";
  // Nothing in here animates any more: the zipline's moving dots were replaced
  // by the scroll-driven rider, which handles reduced motion itself.
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {variant === "skyline" && <Skyline />}
      {variant === "arch" && <Arch stroke={stroke} />}
      {variant === "colonnade" && <Colonnade stroke={stroke} />}
    </div>
  );
}

const BASE = 460;

type Tower = {
  x: number;
  w: number;
  h: number;
  /** setback steps give deco towers their ziggurat shoulders */
  steps?: number;
  cap?: "spire" | "tank" | "flat" | "crown";
};

/* Fixed profiles, not random: a skyline is composed, and it must land the
   same on every reload. Three bands, back to front. */
const FAR: Tower[] = [
  { x: -10, w: 92, h: 150, steps: 1, cap: "flat" },
  { x: 96, w: 64, h: 214, steps: 2, cap: "spire" },
  { x: 172, w: 110, h: 128, cap: "flat" },
  { x: 294, w: 78, h: 196, steps: 2, cap: "tank" },
  { x: 384, w: 128, h: 158, steps: 1, cap: "flat" },
  { x: 524, w: 70, h: 246, steps: 3, cap: "crown" },
  { x: 606, w: 96, h: 140, cap: "flat" },
  { x: 714, w: 82, h: 208, steps: 2, cap: "spire" },
  { x: 808, w: 116, h: 150, steps: 1, cap: "tank" },
  { x: 936, w: 74, h: 226, steps: 2, cap: "flat" },
  { x: 1022, w: 104, h: 168, steps: 1, cap: "flat" },
  { x: 1138, w: 88, h: 232, steps: 2, cap: "spire" },
];

const MID: Tower[] = [
  { x: 30, w: 104, h: 262, steps: 3, cap: "crown" },
  { x: 150, w: 86, h: 196, steps: 1, cap: "tank" },
  { x: 252, w: 122, h: 300, steps: 4, cap: "spire" },
  { x: 392, w: 92, h: 214, steps: 2, cap: "flat" },
  { x: 500, w: 138, h: 268, steps: 3, cap: "tank" },
  { x: 656, w: 88, h: 322, steps: 4, cap: "crown" },
  { x: 762, w: 116, h: 208, steps: 2, cap: "flat" },
  { x: 896, w: 98, h: 286, steps: 3, cap: "spire" },
  { x: 1012, w: 130, h: 224, steps: 2, cap: "tank" },
  { x: 1158, w: 82, h: 264, steps: 3, cap: "flat" },
];

const NEAR: Tower[] = [
  { x: -20, w: 150, h: 168, steps: 1, cap: "tank" },
  { x: 148, w: 118, h: 128, cap: "flat" },
  { x: 286, w: 164, h: 196, steps: 2, cap: "flat" },
  { x: 470, w: 128, h: 142, cap: "tank" },
  { x: 618, w: 178, h: 178, steps: 1, cap: "flat" },
  { x: 816, w: 134, h: 132, cap: "flat" },
  { x: 970, w: 156, h: 186, steps: 2, cap: "tank" },
  { x: 1146, w: 120, h: 146, cap: "flat" },
];

/* Plain block. Deadlock's city is Victorian and Beaux-Arts brownstone, not
   1930s deco, so buildings are straight-sided with a cornice and a roof
   profile rather than stepped ziggurat setbacks. */
function towerPath(t: Tower): string {
  const top = BASE - t.h;
  return `M ${t.x} ${BASE} L ${t.x} ${top} L ${t.x + t.w} ${top} L ${t.x + t.w} ${BASE} Z`;
}

/** Roofline: mansard, cornice slab, or a rooftop water tank. */
function Cap({ t, fill }: { t: Tower; fill: string }) {
  const top = BASE - t.h;
  const cx = t.x + t.w / 2;

  return (
    <g fill={fill}>
      {/* every building gets a cornice band; it is the strongest silhouette
          cue in this architecture */}
      <rect x={t.x - 4} y={top - 7} width={t.w + 8} height="7" />

      {t.cap === "spire" && (
        /* mansard: steep trapezoid roof, the signature Victorian profile */
        <path
          d={`M ${t.x + 4} ${top - 7} L ${t.x + 18} ${top - 34}
              L ${t.x + t.w - 18} ${top - 34} L ${t.x + t.w - 4} ${top - 7} Z`}
        />
      )}
      {t.cap === "crown" && (
        /* corner pavilion with a short mast, as on the hotel blocks */
        <>
          <path
            d={`M ${cx - t.w * 0.3} ${top - 7} L ${cx - t.w * 0.16} ${top - 40}
                L ${cx + t.w * 0.16} ${top - 40} L ${cx + t.w * 0.3} ${top - 7} Z`}
          />
          <rect x={cx - 1.5} y={top - 62} width="3" height="24" />
        </>
      )}
      {t.cap === "tank" && (
        <>
          <path
            d={`M ${cx - 14} ${top - 14} L ${cx - 10} ${top - 34}
                L ${cx + 10} ${top - 34} L ${cx + 14} ${top - 14} Z`}
          />
          <rect x={cx - 12} y={top - 14} width="3" height="8" />
          <rect x={cx + 9} y={top - 14} width="3" height="8" />
        </>
      )}
    </g>
  );
}

/** Warm windows, on a deterministic pattern so the city never reshuffles.

   Every window of a band goes into one path per colour. Drawn as one <path>
   each, the skyline was 765 SVG elements that React built on the first render
   of the homepage -- most of a half-second main-thread block on a mid-range
   phone -- and rebuilt whenever the page re-rendered. The windows never
   overlap and share a fill and opacity within a colour, so one path draws
   exactly the same picture. */
function windowPaths(towers: Tower[], seedBase: number) {
  let warmD = "";
  let coolD = "";
  towers.forEach((t, i) => {
    const seed = seedBase + i;
    const top = BASE - t.h;
    const cols = Math.max(2, Math.floor(t.w / 17));
    const rows = Math.max(2, Math.floor(t.h / 21));
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const n = (r * 13 + c * 7 + seed * 11) % 9;
        if (n > 2) continue; // sparse
        // Amber is the point: warm windows against a cool sky is the only
        // reason a night skyline reads at all. Cool ones are an occasional
        // accent on a separate cycle, not every third window.
        const warm = (r * 5 + c * 3 + seed) % 13 !== 0;
        const x = t.x + 7 + c * 17;
        const y = top + 16 + r * 21;
        // Arched tops, not squares. Round-headed windows are the other
        // signature of this architecture and read even at this size.
        const d = `M ${x} ${y + 5} L ${x} ${y + 1.8} A 1.75 1.75 0 0 1 ${x + 3.5} ${y + 1.8} L ${x + 3.5} ${y + 5} Z `;
        if (warm) warmD += d;
        else coolD += d;
      }
    }
  });
  return { warmD, coolD };
}

function Windows({ towers, seedBase, opacity }: { towers: Tower[]; seedBase: number; opacity: number }) {
  const { warmD, coolD } = windowPaths(towers, seedBase);
  return (
    <g>
      {warmD && <path d={warmD} fill="#f0a95e" opacity={opacity} />}
      {coolD && <path d={coolD} fill="#7fe3d4" opacity={opacity * 0.8} />}
    </g>
  );
}

function Band({
  towers,
  fill,
  windowOpacity,
  seedBase,
}: {
  towers: Tower[];
  fill: string;
  windowOpacity: number;
  seedBase: number;
}) {
  return (
    <g>
      {towers.map((t, i) => (
        <path key={`p${i}`} d={towerPath(t)} fill={fill} />
      ))}
      {towers.map((t, i) => (
        <Cap key={`c${i}`} t={t} fill={fill} />
      ))}
      {windowOpacity > 0 && <Windows towers={towers} seedBase={seedBase} opacity={windowOpacity} />}
    </g>
  );
}

function Skyline() {
  return (
    <svg
      viewBox="0 0 1240 460"
      preserveAspectRatio="xMidYMax slice"
      /* "slice" scales to cover and anchors to the bottom, so the wider the
         window the more it crops off the top -- and what it crops is the
         transparent end of the sky gradient. That left the element's top edge
         starting partway up the gradient, drawing a faint horizontal band the
         full width of the page that got stronger as the window widened.
         Fading the top edge here makes it independent of how much is cropped. */
      className="absolute inset-x-0 bottom-0 h-[70%] w-full [mask-image:linear-gradient(to_bottom,transparent_0%,black_14%)]"
    >
      <defs>
        {/* the sky has to be lighter than the buildings or nothing reads */}
        <linearGradient id="citySky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0b2f33" stopOpacity="0" />
          <stop offset="0.55" stopColor="#0d3a3d" stopOpacity="0.55" />
          <stop offset="1" stopColor="#12484a" stopOpacity="0.8" />
        </linearGradient>
        {/* warm haze at street level, from the art nouveau lamps */}
        <linearGradient id="lampHaze" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f0a95e" stopOpacity="0" />
          <stop offset="1" stopColor="#f0a95e" stopOpacity="0.16" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width="1240" height={BASE} fill="url(#citySky)" />

      {/* back to front: lighter and hazier behind, solid black in front */}
      <Band towers={FAR} fill="#0c2b2f" windowOpacity={0.28} seedBase={1} />
      <Band towers={MID} fill="#071e23" windowOpacity={0.42} seedBase={5} />

      {/* street-level lamp glow sits between the mid and near bands */}
      <rect x="0" y={BASE - 120} width="1240" height="120" fill="url(#lampHaze)" />

      <Band towers={NEAR} fill="#030f14" windowOpacity={0.5} seedBase={9} />

      <ElevatedRail />
    </svg>
  );
}

/** Elevated railway: girder deck on braced trestles, crossing the foreground. */
function ElevatedRail() {
  const y = BASE - 92;
  const bays = Array.from({ length: 11 }, (_, i) => i);
  return (
    <g fill="#030f14">
      <rect x="0" y={y} width="1240" height="11" />
      <rect x="0" y={y + 15} width="1240" height="3" opacity="0.75" />
      {bays.map((i) => {
        const x = 24 + i * 118;
        return (
          <g key={i}>
            <rect x={x} y={y + 11} width="9" height="92" />
            <rect x={x + 96} y={y + 11} width="9" height="92" />
            {/* X-bracing between the posts */}
            <path
              d={`M ${x + 9} ${y + 26} L ${x + 96} ${y + 74} L ${x + 96} ${y + 78} L ${x + 9} ${y + 30} Z`}
              opacity="0.85"
            />
            <path
              d={`M ${x + 96} ${y + 26} L ${x + 9} ${y + 74} L ${x + 9} ${y + 78} L ${x + 96} ${y + 30} Z`}
              opacity="0.85"
            />
          </g>
        );
      })}
    </g>
  );
}

/**
 * Fan window — the round-headed arch with radiating mullions that fills the
 * shrine interiors, rather than a stepped deco arch. The hero render already
 * stands inside an arch, so this frames it the way the game would.
 */
function Arch({ stroke }: { stroke: string }) {
  const cx = 400;
  const cy = 360;
  const R = 250;
  // mullions fan from the springing line, as in the shrine windows
  const spokes = Array.from({ length: 11 }, (_, i) => i);
  return (
    <svg
      viewBox="0 0 800 600"
      className="absolute left-1/2 top-1/2 h-[128%] w-auto min-w-[128%] -translate-x-1/2 -translate-y-1/2"
      fill="none"
    >
      <defs>
        <linearGradient id="archFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={stroke} stopOpacity="0.26" />
          <stop offset="0.62" stopColor={stroke} stopOpacity="0.07" />
          <stop offset="1" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* jambs and the round head */}
      <path
        d={`M ${cx - R} 600 L ${cx - R} ${cy} A ${R} ${R} 0 0 1 ${cx + R} ${cy} L ${cx + R} 600`}
        stroke="url(#archFade)"
        strokeWidth="1.8"
      />
      {/* archivolt, a second band inside the first */}
      <path
        d={`M ${cx - R + 26} 600 L ${cx - R + 26} ${cy} A ${R - 26} ${R - 26} 0 0 1 ${cx + R - 26} ${cy} L ${cx + R - 26} 600`}
        stroke="url(#archFade)"
        strokeWidth="1"
      />

      {/* Radiating mullions. The sweep is inset from the springing angle at
          both ends: a mullion at exactly 0 or PI is horizontal, and the pair
          of them met across the middle of the hero as one stray rule through
          the headline. */}
      {spokes.map((i) => {
        const t = i / (spokes.length - 1); // 0..1
        const a = Math.PI * (0.86 - 0.72 * t); // never reaches 0 or PI
        return (
          <line
            key={i}
            x1={cx + Math.cos(a) * 46}
            y1={cy - Math.sin(a) * 46}
            x2={cx + Math.cos(a) * (R - 30)}
            y2={cy - Math.sin(a) * (R - 30)}
            stroke="url(#archFade)"
            strokeWidth="0.9"
          />
        );
      })}

      {/* concentric transoms */}
      {[92, 152, 208].map((r) => (
        <path
          key={r}
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          stroke="url(#archFade)"
          strokeWidth="0.9"
        />
      ))}

    </svg>
  );
}

/**
 * Zipline rig — the map's traversal network, run down the whole page.
 *
 * In Deadlock the ziplines are the connective tissue of the city: cables
 * strung between rooftop pylons that you ride from lane to lane. Used here
 * they do the same job for the page, carrying continuity down the sections
 * that otherwise had nothing behind them.
 *
 * The cables were previously drawn at rooftop height inside the skyline,
 * which put them straight through the hero copy. Here they run down the side
 * gutters instead, well clear of the reading column.
 */
/** Fluted colonnade — deco facade rhythm, for interior sections. */
function Colonnade({ stroke }: { stroke: string }) {
  const cols = Array.from({ length: 13 }, (_, i) => i);
  return (
    <svg viewBox="0 0 800 300" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" fill="none">
      <defs>
        <linearGradient id="fluteFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={stroke} stopOpacity="0" />
          <stop offset="0.45" stopColor={stroke} stopOpacity="0.14" />
          <stop offset="1" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      {cols.map((i) => {
        const x = 30 + i * 60;
        return (
          <g key={i}>
            <line x1={x} y1="0" x2={x} y2="300" stroke="url(#fluteFade)" strokeWidth="1" />
            <path
              d={`M ${x - 13} 74 L ${x - 13} 66 L ${x - 7} 66 L ${x - 7} 58
                  L ${x + 7} 58 L ${x + 7} 66 L ${x + 13} 66 L ${x + 13} 74`}
              stroke="url(#fluteFade)"
              strokeWidth="1"
            />
          </g>
        );
      })}
    </svg>
  );
}
